/**
 * The network controls of the plan editor — the swatch, the picker and the
 * width field a run is described with. Shared by the layer panel, the run
 * tool's options and the inspector, so "which network" reads the same before
 * drawing, after drawing and in the legend.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './../ui/button'
import { Input } from './../ui/input'
import { NETWORK_LEVELS, type LevelParents } from './engine/network-assets'
import type { NetworkLevel, PlanNetwork, RiserPassage } from './engine/scene'

/**
 * A network's key, drawn exactly as its runs are: the catalogue colour, the
 * catalogue dash, over the run casing. Shared by the layer panel, the run
 * tool's options and the inspector, so the three agree with the stage.
 */
export function NetworkSwatch({ color, dash }: { color?: string; dash?: string }) {
    return (
        <svg width="22" height="8" viewBox="0 0 22 8" aria-hidden="true" className="shrink-0">
            <line x1="2" y1="4" x2="20" y2="4" stroke="var(--plan-run-casing, #ffffff)" strokeWidth="5" strokeLinecap="round" />
            <line
                x1="2" y1="4" x2="20" y2="4"
                stroke={color ?? 'var(--plan-run-fallback, #64748b)'}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray={dash || undefined}
            />
        </svg>
    )
}

/**
 * The network picker — shared by the run tool's options and the inspector,
 * so "which network" reads the same before and after drawing. Options are
 * the catalogue, grouped by family; a code outside it (an imported run) is
 * kept as its own option rather than silently rewritten.
 */
export function NetworkSelect(props: {
    id: string
    testId: string
    networks: PlanNetwork[]
    value: string
    onChange: (code: string) => void
    labelOf: (code: string) => string
}) {
    const { t } = useTranslation('assets')
    const families: string[] = []
    for (const n of props.networks) if (!families.includes(n.family)) families.push(n.family)
    const known = props.networks.some((n) => n.code === props.value)
    const current = props.networks.find((n) => n.code === props.value)
    return (
        <div className="flex flex-1 items-center gap-2">
            <NetworkSwatch color={current?.color} dash={current?.dash} />
            <select
                id={props.id}
                data-testid={props.testId}
                className="h-7 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground"
                value={props.value}
                onChange={(e) => props.onChange(e.target.value)}
            >
                {!props.value && <option value="">{t('planEditor.choose_network')}</option>}
                {props.value && !known && <option value={props.value}>{props.value}</option>}
                {families.map((family) => (
                    <optgroup key={family} label={t(`planEditor.network_families.${family}`, { defaultValue: family })}>
                        {props.networks.filter((n) => n.family === family).map((n) => (
                            <option key={n.code} value={n.code}>{props.labelOf(n.code)}</option>
                        ))}
                    </optgroup>
                ))}
            </select>
        </div>
    )
}

/** Optional millimetre width: blank means "unknown", drawn at screen weight. */
export function RunWidthField(props: { id: string; testId: string; value: number | null; onCommit: (mm: number | null) => void }) {
    const { t } = useTranslation('assets')
    const [draft, setDraft] = useState(props.value ? String(props.value) : '')
    useEffect(() => setDraft(props.value ? String(props.value) : ''), [props.value])
    const commit = () => {
        const trimmed = draft.trim()
        if (!trimmed) {
            if (props.value !== null) props.onCommit(null)
            return
        }
        const parsed = Math.round(Number(trimmed))
        if (!Number.isFinite(parsed) || parsed <= 0) {
            setDraft(props.value ? String(props.value) : '')
            return
        }
        if (parsed !== props.value) props.onCommit(parsed)
    }
    return (
        <Input
            id={props.id}
            data-testid={props.testId}
            type="number"
            className="h-7 text-xs"
            step={10}
            min={1}
            placeholder={t('planEditor.run_width_placeholder')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.preventDefault()
                    commit()
                    ;(e.target as HTMLInputElement).blur()
                } else if (e.key === 'Escape') {
                    e.preventDefault()
                    setDraft(props.value ? String(props.value) : '')
                    ;(e.target as HTMLInputElement).blur()
                }
            }}
        />
    )
}

/**
 * Floor / Building / Site: the level a run's network asset is filed at (spec
 * 2026-10-20-33). A level the frame has no parent for is disabled — a plan
 * drawn on a building has no floor. `value` undefined reads as mixed (a
 * selection spanning levels).
 */
export function RunLevelSwitch(props: {
    testId: string
    value: NetworkLevel | undefined
    parents: LevelParents
    onChange: (level: NetworkLevel) => void
}) {
    const { t } = useTranslation('assets')
    return (
        <div className="flex flex-1 gap-1" role="group" data-testid={props.testId} data-value={props.value ?? ''}>
            {NETWORK_LEVELS.map((level) => {
                const parent = props.parents[level]
                return (
                    <Button
                        key={level}
                        type="button"
                        size="sm"
                        variant={props.value === level ? 'default' : 'outline'}
                        className="h-7 flex-1 px-1 text-[11px]"
                        disabled={!parent}
                        aria-pressed={props.value === level}
                        title={parent?.name}
                        data-testid={`${props.testId}-${level}`}
                        onClick={() => props.onChange(level)}
                    >
                        {t(`planEditor.run_levels.${level}`)}
                    </Button>
                )
            })}
        </div>
    )
}

/** The passages a riser takes, in the order a column reads bottom up. */
export const RISER_PASSAGES: RiserPassage[] = ['origin', 'through', 'terminal']

/**
 * Where the column starts, crosses or ends on this floor. Blank leaves it to
 * the stack: the lowest floor starts it, the highest ends it.
 */
export function RiserPassageSelect(props: {
    id: string
    testId: string
    value: RiserPassage | undefined
    onChange: (passage: RiserPassage | undefined) => void
}) {
    const { t } = useTranslation('assets')
    return (
        <select
            id={props.id}
            data-testid={props.testId}
            className="h-7 w-full flex-1 rounded-md border border-input bg-background px-2 text-xs text-foreground"
            value={props.value ?? ''}
            onChange={(e) => props.onChange((e.target.value || undefined) as RiserPassage | undefined)}
        >
            <option value="">{t('planEditor.riser_passage_derived')}</option>
            {RISER_PASSAGES.map((p) => (
                <option key={p} value={p}>{t(`planEditor.riser_passages.${p}`)}</option>
            ))}
        </select>
    )
}
